import { Fragment, useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check, FileSpreadsheet, FileText, Loader2, Wallet } from 'lucide-react';
import Alert from '../common/Alert';
import MediaCaptureField from '../common/MediaCaptureField.js';
import Button from '../common/Button.js';
import Loader from '../common/Loader.js';
import ListPageHeader from '../common/ListPageHeader.js';
import RowActionsMenu from '../common/RowActionsMenu.js';
import SettlementFilterBar from '../common/SettlementFilterBar.js';
import SettlementModalShell from '../common/SettlementModalShell.js';
import CompanySettlementBatchView from '../common/CompanySettlementBatchView.js';
import { DashboardLayout } from '../common/page_component/index.js';
import { formInputClass, formSelectClass } from '../common/uiClasses.js';
import { fullCurrency } from '../../utils/formatCurrency.js';
import { formatIstDateTime, getCurrentMonthRange } from '../../utils/localDate.js';
import { useAuth } from '../../context/AuthContext.js';
import companySettlementService from '../../services/companySettlementService.js';
import { locationHubService } from '../../services/locationHubService.js';
import HubMultiSelect from '../dashboard/components/HubMultiSelect.js';
import SelectField from '../common/SelectField.js';
import ListPagination from '../common/ListPagination.js';
import { settlementReviewerName } from '../common/SettlementAuditFields.js';
import { PAYMENT_MODES, statusBadge, statusLabel } from '../paid-amount-settlement/paidAmountSettlementUi.js';
import CompanyPaidLines from './CompanyPaidLines.js';

const TABS = [
  { id: 'pending', label: 'Pending', statusParam: 'OCR_PENDING,HUMAN_REVIEW,PENDING' },
  { id: 'approved', label: 'Approved', statusParam: 'APPROVED' },
  { id: 'rejected', label: 'Rejected', statusParam: 'REJECTED' },
  { id: 'company_paid', label: 'Company paid', statusParam: '' },
  { id: 'company_owes', label: 'Company owes', statusParam: '' },
];

const OWES_PAGE_SIZE = 10;

const dateParams = (fromDate, toDate) => {
  const params = {};
  if (fromDate) params.from_date = fromDate;
  if (toDate) params.to_date = toDate;
  return params;
};

const hubIdsFromSearch = (searchParams) => {
  const raw = searchParams.get('hub_ids') || searchParams.get('location_hub_ids') || '';
  return raw.split(',').map((id) => id.trim()).filter(Boolean);
};

const dashboardBackTo = (role) => {
  if (role === 'Accountant') return '/accountant-dashboard';
  if (role === 'System Admin') return '/system-admin-dashboard';
  return '/admin-dashboard';
};

const canReview = (status) => status === 'HUMAN_REVIEW' || status === 'PENDING';

const reviewFields = (row) => {
  const nested = (row.paid_requests || []).concat(row.to_pay_requests || [])[0] || {};
  return {
    ...nested,
    ...row,
    status: row.status,
    reviewed_at: row.reviewed_at || nested.reviewed_at,
    reviewed_by_display: row.reviewed_by_display || nested.reviewed_by_display,
    reviewed_by_name: row.reviewed_by_name || nested.reviewed_by_name,
  };
};

const AccountantCompanySettlementApprovals = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const month = getCurrentMonthRange();
  const [tab, setTab] = useState('pending');
  const [fromDate, setFromDate] = useState(() => searchParams.get('from_date') || month.fromDate);
  const [toDate, setToDate] = useState(() => searchParams.get('to_date') || month.toDate);
  const [hubIds, setHubIds] = useState(() => hubIdsFromSearch(searchParams));
  const [agentId, setAgentId] = useState(() => (searchParams.get('agent_user_id') || '').trim());
  const [agentOptions, setAgentOptions] = useState([]);
  const [hubs, setHubs] = useState([]);
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0, company_paid: 0, company_owes: 0 });
  const [owesPage, setOwesPage] = useState(1);
  const [review, setReview] = useState(null);
  const [companyPayments, setCompanyPayments] = useState([]);
  const [owesAgents, setOwesAgents] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [payAgentId, setPayAgentId] = useState('');
  const [payMode, setPayMode] = useState('cash');
  const [payRemarks, setPayRemarks] = useState('');
  const [payFile, setPayFile] = useState(null);
  const [paying, setPaying] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [viewBatch, setViewBatch] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);

  const statusParam = TABS.find((t) => t.id === tab)?.statusParam || '';

  const filterParams = useCallback(() => ({
    ...dateParams(fromDate, toDate),
    ...(hubIds.length ? { location_hub_ids: hubIds } : {}),
    ...(agentId ? { agent_user_id: agentId } : {}),
  }), [fromDate, toDate, hubIds, agentId]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = filterParams();
      const [sumRes, listRes] = await Promise.all([
        companySettlementService.getSummary(params),
        companySettlementService.listBatches({
          status: statusParam,
          ...params,
        }),
      ]);
      const summary = sumRes?.data || {};
      const owed = summary.company_owes_agents || [];
      setReview(summary.review || null);
      setOwesAgents(owed);
      setRows(listRes?.data || []);
      const payments = listRes?.company_payments || [];
      setCompanyPayments(payments);
      const agents = new Map();
      (listRes?.filter_options?.agents || []).forEach((agent) => {
        agents.set(String(agent.user_id), agent);
      });
      owed.forEach((row) => {
        const id = String(row.user_id);
        if (!agents.has(id)) {
          agents.set(id, {
            user_id: row.user_id,
            name: row.name,
            location_hub_name: row.hub_name || '',
          });
        }
      });
      setAgentOptions([...agents.values()]);
      setCounts({
        ...(listRes?.counts || { pending: 0, approved: 0, rejected: 0 }),
        company_paid: payments.length,
        company_owes: owed.length,
      });
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to load settlements');
    } finally {
      setLoading(false);
    }
  }, [statusParam, filterParams]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setOwesPage(1);
  }, [fromDate, toDate, hubIds, agentId]);

  useEffect(() => {
    locationHubService.getAllHubs(1, 500, { all: true })
      .then((res) => {
        const data = res.data || {};
        setHubs(data.hubs || data.items || data.location_hubs || data.data || []);
      })
      .catch(() => setHubs([]));
  }, []);

  const onApprove = async (batchId) => {
    try {
      setBusyId(batchId);
      const res = await companySettlementService.approveBatch(batchId);
      Alert.success(res?.message || 'Approved');
      setViewBatch(null);
      await loadData();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Approve failed');
    } finally {
      setBusyId('');
    }
  };

  const onReject = async (batchId) => {
    const notes = window.prompt('Rejection notes (optional)');
    if (notes === null) return;
    try {
      setBusyId(batchId);
      const res = await companySettlementService.rejectBatch(batchId, { review_notes: notes });
      Alert.success(res?.message || 'Rejected');
      setViewBatch(null);
      await loadData();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Reject failed');
    } finally {
      setBusyId('');
    }
  };

  const paySelected = owesAgents.find((row) => String(row.user_id) === String(payAgentId)) || owesAgents[0];

  const onPayAgent = async () => {
    const agent = owesAgents.find((row) => String(row.user_id) === String(payAgentId)) || owesAgents[0];
    if (!agent) {
      Alert.error('Select the agent to pay');
      return;
    }
    if (!payFile) {
      Alert.error('Attach the payment image');
      return;
    }
    try {
      setPaying(true);
      const form = new FormData();
      form.append('agent_user_id', agent.user_id);
      form.append('from_date', fromDate || '');
      form.append('to_date', toDate || '');
      if (hubIds.length) form.append('location_hub_ids', hubIds.join(','));
      form.append('payment_mode', payMode);
      form.append('remarks', payRemarks || '');
      form.append('file_attachment', payFile);
      const res = await companySettlementService.payAgent(form);
      Alert.success(res?.message || 'Payment recorded');
      setPayOpen(false);
      setPayRemarks('');
      setPayFile(null);
      await loadData();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Payment failed');
    } finally {
      setPaying(false);
    }
  };

  const exportParams = {
    status: statusParam,
    ...filterParams(),
  };

  const downloadExcel = async () => {
    try {
      await companySettlementService.downloadExcel(exportParams);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Excel download failed');
    }
  };

  const downloadPdf = async () => {
    try {
      await companySettlementService.downloadPdf(exportParams);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'PDF download failed');
    }
  };

  const openView = async (row) => {
    try {
      const res = await companySettlementService.getBatch(row.batch_id);
      setViewBatch(res?.data || row);
    } catch (err) {
      setViewBatch(row);
    }
  };

  const owesTotalPages = Math.max(1, Math.ceil(owesAgents.length / OWES_PAGE_SIZE));
  const owesPageSafe = Math.min(owesPage, owesTotalPages);
  const owesSlice = owesAgents.slice(0, owesPageSafe * OWES_PAGE_SIZE);
  const canSettleOwes = user?.role !== 'Hub Admin';

  const payToAgents = owesAgents.reduce((sum, row) => sum + Number(row.amount || 0), 0);

  const dueToCompany = Number(review?.due_to_company || 0);
  const paidDue = Number(review?.paid_due || 0);
  const toPayDue = Number(review?.to_pay_due || 0);
  const remitted = Number(review?.approved_paid_amount || 0);
  const pendingCount = Number(review?.pending_review_count || 0);
  const pendingAmount = Number(review?.pending_review_amount || 0);

  return (
    <DashboardLayout>
      <ListPageHeader
        title="Settlement Approvals"
        subtitle={
          user?.role === 'Hub Admin'
            ? 'Open dues for this hub. Remit them on Settle with Company. This page is for requests waiting on accountant approve.'
            : 'One approve settles Paid remittance and To-Pay remittance. Cashbook posts after approve.'
        }
        icon={Check}
        backTo={dashboardBackTo(user?.role)}
      >
        <Button variant="secondary" icon={FileSpreadsheet} mobileIconOnly onClick={downloadExcel}>
          Excel
        </Button>
        <Button variant="secondary" icon={FileText} mobileIconOnly onClick={downloadPdf}>
          PDF
        </Button>
      </ListPageHeader>

      <SettlementFilterBar
        statusLabel="Status"
        tabs={TABS}
        activeTab={tab}
        onTabChange={setTab}
        counts={counts}
        fromDate={fromDate}
        toDate={toDate}
        onFromDate={setFromDate}
        onToDate={setToDate}
        centerStatus
        singleRow
        extraLeft={(
          <div className="flex w-full min-w-0 shrink-0 flex-col gap-3 sm:w-auto sm:flex-row sm:items-end">
            <div className="w-full min-w-0 sm:w-44">
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Hub / Branch</label>
              <HubMultiSelect
                hubs={hubs}
                value={hubIds}
                onChange={setHubIds}
                placeholder="All Hubs"
                compact
                className="!w-full !min-w-0 !max-w-none"
                buttonClassName="!min-w-0 !max-w-none"
              />
            </div>
            <div className="w-full min-w-0 sm:w-48">
              <SelectField
                variant="filter"
                portal
                label="Agent"
                labelClassName="block text-xs font-medium text-gray-500 mb-1.5"
                value={agentId}
                onChange={(e) => setAgentId(e.target.value)}
                placeholder="All agents"
                emptyLabel="All agents"
                options={[
                  { value: '', label: 'All agents' },
                  ...agentOptions.map((agent) => {
                    const hub = String(agent.location_hub_name || '').trim();
                    const name = agent.name || agent.user_id;
                    return {
                      value: String(agent.user_id),
                      label: hub ? `${name} (${hub})` : name,
                    };
                  }),
                ]}
              />
            </div>
          </div>
        )}
      />

      <div className="mb-4 grid grid-cols-2 gap-3 xl:grid-cols-5">
          <div className="min-w-0 bg-white rounded-xl border border-amber-100 p-3 sm:p-4">
            <p className="text-xs text-gray-500">Due to company</p>
            <p className="mt-1 text-lg font-semibold text-amber-800 tabular-nums">{fullCurrency(dueToCompany)}</p>
          </div>
          <div className="min-w-0 bg-white rounded-xl border border-sky-100 p-3 sm:p-4">
            <p className="text-xs text-gray-500">Paid / To-Pay due</p>
            <p className="mt-1 text-lg font-semibold text-sky-800 tabular-nums">{fullCurrency(paidDue + toPayDue)}</p>
            <p className="text-[11px] text-gray-500 mt-0.5">
              Paid {fullCurrency(paidDue)} · To-Pay {fullCurrency(toPayDue)}
            </p>
          </div>
          <div className="min-w-0 bg-white rounded-xl border border-emerald-100 p-3 sm:p-4">
            <p className="text-xs text-gray-500">Remitted (approved)</p>
            <p className="mt-1 text-lg font-semibold text-emerald-800 tabular-nums">{fullCurrency(remitted)}</p>
          </div>
          <div className="min-w-0 bg-white rounded-xl border border-orange-100 p-3 sm:p-4">
            <p className="text-xs text-gray-500">Pending review</p>
            <p className="mt-1 text-lg font-semibold text-orange-800 tabular-nums">{fullCurrency(pendingAmount)}</p>
            <p className="text-[11px] text-gray-500 mt-0.5">
              {pendingCount} request{pendingCount === 1 ? '' : 's'}
            </p>
          </div>
          <div className="min-w-0 bg-white rounded-xl border border-emerald-300 p-3 sm:p-4">
            <p className="text-[10px] sm:text-xs text-gray-500 leading-tight">Pay to agents</p>
            <p className="text-sm sm:text-lg font-semibold text-emerald-800 tabular-nums leading-tight">
              {fullCurrency(payToAgents)}
            </p>
            <button
              type="button"
              className="mt-1 text-[10px] sm:text-xs font-semibold text-emerald-800 underline"
              onClick={() => setTab('company_owes')}
            >
              {owesAgents.length} agent{owesAgents.length === 1 ? '' : 's'}
            </button>
          </div>
      </div>

      <p className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
        <span className="font-semibold text-blue-700">
          {tab === 'company_owes' || tab === 'company_paid'
            ? `${owesAgents.length} agent${owesAgents.length === 1 ? '' : 's'} to pay`
            : `${Number(counts[tab]) || 0} request${(Number(counts[tab]) || 0) === 1 ? '' : 's'} in ${TABS.find((item) => item.id === tab)?.label?.toLowerCase() || tab}`}
        </span>
        <span>
          Period total {(Number(counts.pending) || 0) + (Number(counts.approved) || 0) + (Number(counts.rejected) || 0)}
          {' · '}Pending {counts.pending || 0}
          {' · '}Approved {counts.approved || 0}
          {' · '}Rejected {counts.rejected || 0}
        </span>
      </p>

      {loading ? (
        <Loader fullScreen={false} message="Loading requests..." />
      ) : tab === 'company_owes' || tab === 'company_paid' ? (
        <>
        <div className="mb-4 bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-800">Company owes</h2>
            <p className="text-xs text-gray-500 mt-0.5">Commission the company still has to pay. Settle opens the payment, with the ML sheet and amount. Proof is required.</p>
          </div>
          {!owesAgents.length ? (
            <p className="text-sm text-gray-400 text-center py-10">No agent to pay in this period</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="text-left px-4 py-2.5 font-semibold">Agent</th>
                      <th className="text-left px-4 py-2.5 font-semibold">Hub / Branch</th>
                      <th className="text-left px-4 py-2.5 font-semibold">From</th>
                      <th className="text-right px-4 py-2.5 font-semibold">Amount</th>
                      <th className="text-right px-4 py-2.5 font-semibold"> </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {owesSlice.map((row) => (
                      <tr key={row.user_id}>
                        <td className="px-4 py-2.5 font-medium text-gray-900">{row.name || row.user_id}</td>
                        <td className="px-4 py-2.5 text-gray-700">{row.hub_name || '—'}</td>
                        <td className="px-4 py-2.5 text-gray-700">
                          {(row.sources || []).length ? (
                            <div className="space-y-0.5">
                              {row.sources.map((source) => (
                                <p key={`${row.user_id}-${source.ml_sheet_id}`} className="leading-tight">
                                  <span className="font-mono text-xs">{source.ml_sheet_id || '—'}</span>
                                  {source.location ? ` · ${source.location}` : ''}
                                  <span className="text-gray-500"> · {fullCurrency(source.amount)}</span>
                                </p>
                              ))}
                            </div>
                          ) : '—'}
                        </td>
                        <td className="px-4 py-2.5 text-right font-semibold text-emerald-800 tabular-nums">{fullCurrency(row.amount)}</td>
                        <td className="px-4 py-2.5 text-right">
                          {canSettleOwes ? (
                            <button
                              type="button"
                              title="Pay agent"
                              aria-label="Pay agent"
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-emerald-700 hover:bg-emerald-50"
                              onClick={() => {
                                setPayAgentId(row.user_id);
                                setPayOpen(true);
                              }}
                            >
                              <Wallet className="w-4 h-4" />
                            </button>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ListPagination
                currentPage={owesPageSafe}
                totalPages={owesTotalPages}
                onNext={() => setOwesPage((page) => page + 1)}
              />
            </>
          )}
        </div>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-800">What we paid</h2>
            <p className="text-xs text-gray-500 mt-0.5">Payments already sent to agents in this period.</p>
          </div>
          {!companyPayments.length ? (
            <p className="text-sm text-gray-400 text-center py-10">No company payment in this period</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-left px-4 py-2.5 font-semibold">Payment</th>
                    <th className="text-left px-4 py-2.5 font-semibold">Agent</th>
                    <th className="text-left px-4 py-2.5 font-semibold">Date</th>
                    <th className="text-left px-4 py-2.5 font-semibold">Mode</th>
                    <th className="text-right px-4 py-2.5 font-semibold">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {companyPayments.map((row) => (
                    <tr key={row.expense_id}>
                      <td className="px-4 py-2.5 font-mono text-xs">{row.expense_id}</td>
                      <td className="px-4 py-2.5">{row.paid_to || row.paid_user_id || '—'}</td>
                      <td className="px-4 py-2.5">{formatIstDateTime(row.payment_date || row.expense_date) || '—'}</td>
                      <td className="px-4 py-2.5">{row.payment_mode || '—'}</td>
                      <td className="px-4 py-2.5 text-right">
                        <CompanyPaidLines row={row} align="right" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          {!rows.length ? (
            <p className="text-sm text-gray-400 text-center py-10">No requests in this tab</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[920px] text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-left px-4 py-2.5 font-semibold">Batch</th>
                    <th className="text-left px-4 py-2.5 font-semibold">Agent</th>
                    <th className="text-left px-4 py-2.5 font-semibold">Hub / Branch</th>
                    <th className="text-left px-4 py-2.5 font-semibold">Status</th>
                    <th className="text-right px-4 py-2.5 font-semibold">Paid</th>
                    <th className="text-right px-4 py-2.5 font-semibold">To Pay</th>
                    <th className="text-right px-4 py-2.5 font-semibold">Total</th>
                    <th className="text-left px-4 py-2.5 font-semibold">
                      {tab === 'rejected' ? 'Rejected by' : 'Approved by'}
                    </th>
                    <th className="text-right px-4 py-2.5 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((row) => {
                    const decided = row.status === 'APPROVED' || row.status === 'REJECTED';
                    const review = reviewFields(row);
                    return (
                    <Fragment key={row.batch_id}>
                      <tr className="hover:bg-sky-50/40 cursor-pointer" onClick={() => openView(row)}>
                        <td className="px-4 py-2.5 font-mono text-xs">
                          <button type="button" className="underline" onClick={() => openView(row)}>
                            {row.batch_id}
                          </button>
                        </td>
                        <td className="px-4 py-2.5">
                          <p className="font-medium text-gray-900 leading-tight">{row.agent_name}</p>
                          <p className="text-[11px] text-gray-500 mt-0.5 leading-tight">
                            {formatIstDateTime(row.submitted_at)}
                          </p>
                        </td>
                        <td className="px-4 py-2.5 text-gray-700">{row.hub_label || '—'}</td>
                        <td className="px-4 py-2.5">
                          <span className={`inline-flex px-2 py-0.5 rounded-full border text-xs ${statusBadge(row.status)}`}>
                            {statusLabel(row.status)}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{fullCurrency(row.paid_amount)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{fullCurrency(row.to_pay_amount)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums font-medium">{fullCurrency(row.total_amount)}</td>
                        <td className="px-4 py-2.5">
                          {decided ? (
                            <>
                              <p className="text-gray-900 leading-tight">{settlementReviewerName(review)}</p>
                              <p className="text-[11px] text-gray-500 mt-0.5 leading-tight">
                                {formatIstDateTime(review.reviewed_at)}
                              </p>
                            </>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-center" onClick={(e) => e.stopPropagation()}>
                          <RowActionsMenu
                            className="relative menu-container inline-flex"
                            isOpen={openMenuId === row.batch_id}
                            onToggle={() => setOpenMenuId(openMenuId === row.batch_id ? null : row.batch_id)}
                            onClose={() => setOpenMenuId(null)}
                            onView={() => openView(row)}
                            extraItems={canReview(row.status) && busyId !== row.batch_id ? [
                              {
                                key: 'approve',
                                icon: '✅',
                                label: 'Approve',
                                variant: 'success',
                                onClick: () => onApprove(row.batch_id),
                              },
                              {
                                key: 'reject',
                                icon: '❌',
                                label: 'Reject',
                                variant: 'danger',
                                onClick: () => onReject(row.batch_id),
                              },
                            ] : []}
                          />
                        </td>
                      </tr>
                    </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {payOpen ? (
        <SettlementModalShell
          title="Pay agent"
          subtitle="Every open amount in this period is paid together, with one receipt."
          size="md"
          onClose={() => { if (!paying) { setPayOpen(false); setPayFile(null); } }}
          footer={(
            <div className="flex justify-end gap-2 flex-wrap">
              <Button variant="secondary" disabled={paying} onClick={() => { setPayOpen(false); setPayFile(null); }}>Cancel</Button>
              <Button variant="primary" icon={paying ? undefined : Wallet} disabled={paying || !payFile} onClick={onPayAgent}>
                {paying ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                {paying ? 'Paying…' : 'Pay and settle'}
              </Button>
            </div>
          )}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Agent</label>
              <select
                value={payAgentId || paySelected?.user_id || ''}
                onChange={(e) => setPayAgentId(e.target.value)}
                className={formSelectClass()}
              >
                {owesAgents.map((row) => (
                  <option key={row.user_id} value={row.user_id}>
                    {row.name} · {fullCurrency(row.amount)}
                  </option>
                ))}
              </select>
            </div>
            {(paySelected?.sources || []).length ? (
              <div className="rounded-xl border border-gray-100 bg-gray-50 px-3 py-3 space-y-1">
                <p className="text-xs font-medium text-gray-600">Records in this period</p>
                {paySelected.sources.map((source) => (
                  <p key={`${paySelected.user_id}-${source.ml_sheet_id}`} className="text-sm text-gray-800 flex justify-between gap-3">
                    <span>
                      <span className="font-mono text-xs">{source.ml_sheet_id || 'Record'}</span>
                      {source.location ? ` · ${source.location}` : ''}
                    </span>
                    <span className="tabular-nums font-medium">{fullCurrency(source.amount)}</span>
                  </p>
                ))}
              </div>
            ) : null}
            <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-3 py-3">
              <p className="text-xs text-emerald-800">One payment for all of these</p>
              <p className="text-lg font-semibold text-emerald-900 tabular-nums">
                {fullCurrency(paySelected?.amount)}
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Payment image</label>
              <MediaCaptureField mode="document" onPick={(file) => setPayFile(file || null)} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Payment mode</label>
              <select value={payMode} onChange={(e) => setPayMode(e.target.value)} className={formSelectClass()}>
                {PAYMENT_MODES.map((mode) => (
                  <option key={mode.value} value={mode.value}>{mode.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Remarks</label>
              <input
                value={payRemarks}
                onChange={(e) => setPayRemarks(e.target.value)}
                className={formInputClass()}
                placeholder="Optional"
              />
            </div>
          </div>
        </SettlementModalShell>
      ) : null}

      {viewBatch ? (
        <CompanySettlementBatchView
          batch={viewBatch}
          onClose={() => setViewBatch(null)}
          canReview={canReview(viewBatch.status)}
          busy={busyId === viewBatch.batch_id}
          onApprove={onApprove}
          onReject={onReject}
        />
      ) : null}
    </DashboardLayout>
  );
};

export default AccountantCompanySettlementApprovals;
